import React, { useState } from 'react';
import { saveDownload } from '../utils/downloads';

/**
 * "Historial" card shared by PropertyDetail, RoomDetail and VacationalDetail.
 *
 * Shows one year at a time (‹ 2026 ›), months from newest to oldest, with
 * columns Mes / Estado / Ingresos / Gastos / Neto. Each page only tells the
 * card how to compute a month through `computeMonth(year, month)`:
 *   → { income, expenses, status: { label, active } }
 *
 * Props:
 *  - name:          property / room name (used in exports)
 *  - exportTitle:   title prefix for the exports (default "Historial")
 *  - startYear, startMonth: first month with data (left arrow stops at startYear)
 *  - computeMonth:  (year, month) => row data
 *  - onSelectMonth: optional (year, month) => void when a row is tapped
 */

const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export const monthIndex = (year, month) => year * 12 + month;

// Month (as monthIndex) a tenant started living in the property.
// Tenants carry no contract dates; their id is a Date.now() timestamp taken
// when they were added, which is the best start date we have.
export function getTenantStartIndex(tenant) {
  if (!tenant) return null;
  const raw = tenant.startDate || tenant.contractStart || tenant.createdAt || tenant.created_at;
  if (raw) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return monthIndex(d.getFullYear(), d.getMonth());
  }
  const ts = Number(tenant.id);
  // Plausible timestamp: after 2010 and not in the future
  if (Number.isFinite(ts) && ts > 1262304000000 && ts <= Date.now() + 86400000) {
    const d = new Date(ts);
    return monthIndex(d.getFullYear(), d.getMonth());
  }
  return null;
}

// Earliest month among a list of dates (Date | string | null). Defaults to the current month.
export function earliestMonth(dates) {
  const valid = dates
    .map(d => (d instanceof Date ? d : (d ? new Date(d) : null)))
    .filter(d => d && !isNaN(d.getTime()));
  const now = new Date();
  if (valid.length === 0) return { year: now.getFullYear(), month: now.getMonth() };
  const min = new Date(Math.min(...valid.map(d => d.getTime())));
  // Never later than today
  if (min > now) return { year: now.getFullYear(), month: now.getMonth() };
  return { year: min.getFullYear(), month: min.getMonth() };
}

const fmtSigned = (v, sign) => `${sign}${Math.abs(v).toFixed(0)} €`;
const fmtNet = v => `${v >= 0 ? '+' : '-'}${Math.abs(v).toFixed(0)} €`;
const safeFileName = s => String(s || 'propiedad').replace(/\s+/g, '_').replace(/[^\w\-áéíóúñÁÉÍÓÚÑ]/g, '');

async function exportYearToExcel({ name, exportTitle, year, rows, total }) {
  const ExcelJSMod = await import('exceljs');
  const ExcelJS = ExcelJSMod.default || ExcelJSMod;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(`Historial ${year}`, { views: [{ showGridLines: false }] });
  const euro = '#,##0.00 "€"';
  const mkFill = argb => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

  const tRow = ws.addRow([`${exportTitle.toUpperCase()} ${year} — ${name}`]); tRow.height = 30;
  ws.mergeCells(1, 1, 1, 5);
  tRow.getCell(1).fill = mkFill('FF111111');
  tRow.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
  tRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };

  const dRow = ws.addRow([`Exportado el ${new Date().toLocaleDateString('es-ES')}`]); dRow.height = 17;
  ws.mergeCells(2, 1, 2, 5);
  dRow.getCell(1).fill = mkFill('FF111111');
  dRow.getCell(1).font = { size: 8, italic: true, color: { argb: 'FFAAAAAA' } };
  dRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };

  ws.addRow([]).height = 6;

  const hRow = ws.addRow(['Mes', 'Estado', 'Ingresos', 'Gastos', 'Neto']); hRow.height = 20;
  hRow.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = mkFill('FFF0F0F0');
    cell.font = { bold: true, color: { argb: 'FF555555' }, size: 9 };
    cell.alignment = { horizontal: col <= 2 ? 'left' : 'right', vertical: 'middle' };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } } };
  });

  rows.forEach((item, idx) => {
    const bg = mkFill(idx % 2 === 0 ? 'FFFFFFFF' : 'FFF9F9F9');
    const bdr = { bottom: { style: 'thin', color: { argb: 'FFF0F0F0' } } };
    const r = ws.addRow([`${MONTH_NAMES[item.month]} ${item.year}`, item.status.label, item.income, -item.expenses, item.net]);
    r.height = 20;
    for (let c = 1; c <= 5; c++) {
      r.getCell(c).fill = bg;
      r.getCell(c).alignment = { horizontal: c <= 2 ? 'left' : 'right', vertical: 'middle' };
      r.getCell(c).border = bdr;
    }
    r.getCell(2).font = { color: { argb: item.status.active ? 'FF2E7D32' : 'FF999999' }, size: 9 };
    r.getCell(3).font = { color: { argb: 'FF2E7D32' } }; r.getCell(3).numFmt = euro;
    r.getCell(4).font = { color: { argb: 'FFC62828' } }; r.getCell(4).numFmt = euro;
    r.getCell(5).font = { bold: true, color: { argb: item.net >= 0 ? 'FF2E7D32' : 'FFC62828' } }; r.getCell(5).numFmt = euro;
  });

  ws.addRow([]).height = 6;

  const totRowNum = ws.rowCount + 1;
  const totRow = ws.addRow([`Ganancia neta ${year}`, '', '', '', total]); totRow.height = 26;
  ws.mergeCells(totRowNum, 1, totRowNum, 4);
  for (let c = 1; c <= 5; c++) {
    totRow.getCell(c).fill = mkFill('FF111111');
    totRow.getCell(c).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    totRow.getCell(c).alignment = { horizontal: c === 1 ? 'left' : 'right', vertical: 'middle' };
  }
  totRow.getCell(5).font = { bold: true, size: 12, color: { argb: total >= 0 ? 'FF90EE90' : 'FFFF8080' } };
  totRow.getCell(5).numFmt = euro;

  [18, 16, 14, 14, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveDownload(blob, `historial_${safeFileName(name)}_${year}.xlsx`, { source: 'Historial', propertyName: name });
}

async function exportYearToPDF({ name, exportTitle, year, rows, total }) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30);
  doc.text(`${exportTitle} ${year} — ${name}`, 14, 20);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(150);
  doc.text(`Exportado el ${new Date().toLocaleDateString('es-ES')}`, 14, 28);

  // Mes / Estado left-aligned; amounts right-aligned on their column edge
  const colX = [14, 58, 130, 160, 196];
  let y = 42;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('MES', colX[0], y);
  doc.text('ESTADO', colX[1], y);
  doc.text('INGRESOS', colX[2], y, { align: 'right' });
  doc.text('GASTOS', colX[3], y, { align: 'right' });
  doc.text('NETO', colX[4], y, { align: 'right' });
  doc.setDrawColor(220);
  doc.line(14, y + 3, 196, y + 3);
  y += 12;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  rows.forEach(item => {
    doc.setTextColor(50);
    doc.text(`${MONTH_NAMES[item.month]} ${item.year}`, colX[0], y);
    item.status.active ? doc.setTextColor(46, 125, 50) : doc.setTextColor(150);
    doc.text(item.status.label, colX[1], y);
    doc.setTextColor(76, 175, 80);
    doc.text(fmtSigned(item.income, '+'), colX[2], y, { align: 'right' });
    doc.setTextColor(244, 67, 54);
    doc.text(fmtSigned(item.expenses, '-'), colX[3], y, { align: 'right' });
    item.net >= 0 ? doc.setTextColor(76, 175, 80) : doc.setTextColor(244, 67, 54);
    doc.text(fmtNet(item.net), colX[4], y, { align: 'right' });
    doc.setDrawColor(240);
    doc.line(14, y + 3, 196, y + 3);
    y += 10;
  });

  y += 4;
  doc.setDrawColor(50);
  doc.line(14, y, 196, y);
  y += 8;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(50);
  doc.text(`Ganancia neta ${year}`, colX[0], y);
  total >= 0 ? doc.setTextColor(76, 175, 80) : doc.setTextColor(244, 67, 54);
  doc.text(fmtNet(total), colX[4], y, { align: 'right' });

  saveDownload(doc.output('blob'), `historial_${safeFileName(name)}_${year}.pdf`, { source: 'Historial', propertyName: name });
}

const FileIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function MonthlyHistoryCard({
  name,
  exportTitle = 'Historial',
  startYear,
  startMonth = 0,
  computeMonth,
  onSelectMonth,
}) {
  const now = new Date();
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth();
  const minYear = Math.min(startYear ?? nowYear, nowYear);
  const minMonthIdx = monthIndex(minYear, minYear === startYear ? startMonth : 0);

  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(nowYear);
  const [exporting, setExporting] = useState(false);

  // Keep the selected year inside [minYear, nowYear] if the data range changes
  const shownYear = Math.max(minYear, Math.min(year, nowYear));

  const buildRows = () => {
    const rows = [];
    const lastMonth = shownYear === nowYear ? nowMonth : 11;
    for (let m = lastMonth; m >= 0; m--) {
      if (monthIndex(shownYear, m) < minMonthIdx) break;
      const r = computeMonth(shownYear, m);
      const income = r.income || 0;
      const expenses = r.expenses || 0;
      rows.push({ year: shownYear, month: m, income, expenses, net: income - expenses, status: r.status || { label: '—', active: false } });
    }
    return rows;
  };

  const rows = open ? buildRows() : [];
  const total = rows.reduce((s, r) => s + r.net, 0);

  const runExport = async (fn) => {
    if (exporting) return;
    setExporting(true);
    try {
      await fn({ name, exportTitle, year: shownYear, rows, total });
    } catch (err) {
      console.error('Error exportando historial:', err);
      alert('No se pudo generar el archivo. Inténtalo de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="info-card history-card">
      <div className="card-header clickable" onClick={() => setOpen(o => !o)}>
        <h3>Historial</h3>
        <span className="arrow">{open ? '▼' : '›'}</span>
      </div>

      {open && (
        <div className="history-detail">
          {/* Selector de año */}
          <div className="mhc-year-nav">
            <div className="month-nav-row">
              <div className="month-nav-side month-nav-left">
                <button className="month-nav-btn" onClick={() => setYear(shownYear - 1)} disabled={shownYear <= minYear} aria-label="Año anterior">‹</button>
              </div>
              <span className="month-label">{shownYear}</span>
              <div className="month-nav-side month-nav-right">
                <button className="month-nav-btn" onClick={() => setYear(shownYear + 1)} disabled={shownYear >= nowYear} aria-label="Año siguiente">›</button>
              </div>
            </div>
          </div>

          <div className="export-buttons">
            <button className="export-btn excel" disabled={exporting || rows.length === 0} onClick={() => runExport(exportYearToExcel)}>
              <FileIcon />
              Exportar Excel
            </button>
            <button className="export-btn pdf" disabled={exporting || rows.length === 0} onClick={() => runExport(exportYearToPDF)}>
              <FileIcon />
              Exportar PDF
            </button>
          </div>

          <div className="mhc-table" role="table" aria-label={`Historial ${shownYear}`}>
            <div className="mhc-row mhc-head" role="row">
              <span role="columnheader">Mes</span>
              <span role="columnheader">Estado</span>
              <span role="columnheader" className="mhc-num">Ingresos</span>
              <span role="columnheader" className="mhc-num">Gastos</span>
              <span role="columnheader" className="mhc-num">Neto</span>
            </div>

            {rows.length === 0 && (
              <p className="mhc-empty">Sin datos para {shownYear}.</p>
            )}

            {rows.map(item => (
              <div
                key={`${item.year}-${item.month}`}
                role="row"
                className={`mhc-row${onSelectMonth ? ' mhc-clickable' : ''}`}
                onClick={onSelectMonth ? () => onSelectMonth(item.year, item.month) : undefined}
              >
                <span role="cell" className="mhc-month">
                  <span className="mhc-long">{MONTH_NAMES[item.month]}</span>
                  <span className="mhc-short">{MONTH_SHORT[item.month]}</span>
                </span>
                <span role="cell" className={`mhc-status${item.status.active ? ' active' : ''}`}>{item.status.label}</span>
                <span role="cell" className="mhc-num mhc-income">{item.income > 0 ? fmtSigned(item.income, '+') : '—'}</span>
                <span role="cell" className="mhc-num mhc-expenses">{item.expenses > 0 ? fmtSigned(item.expenses, '-') : '—'}</span>
                <span role="cell" className={`mhc-num mhc-net ${item.net >= 0 ? 'positive' : 'negative'}`}>{fmtNet(item.net)}</span>
              </div>
            ))}
          </div>

          <div className="history-accumulated">
            <span>Ganancia neta {shownYear}</span>
            <span className={total >= 0 ? 'positive' : 'negative'}>{fmtNet(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
